/**
 * useABVariant — split traffic 50/50 across A and B variants for the hero CTA.
 *
 * Persisted in `localStorage[alp.ab.<key>]` so the same visitor always sees
 * the same copy. First impression and every click are reported to
 * /api/analytics/landing-event for funnel analysis.
 *
 * Usage:
 *   const { variant, copy, trackClick } = useABVariant("hero-primary", {
 *     A: "Start your FREE TRIAL",
 *     B: "See it find leads in 60s",
 *   });
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VID_KEY = "alp.visitor_id";

function getOrCreateVisitorId() {
  try {
    let v = window.localStorage.getItem(VID_KEY);
    if (!v) {
      v = (window.crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      window.localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch (_e) { return `v_${Date.now()}`; }
}

function fireEvent(payload) {
  // Fire-and-forget — analytics must never break the UI.
  try {
    axios.post(`${API}/analytics/landing-event`, payload).catch(() => {});
    // Iter58 — optional PostHog fan-out for session-replay heatmaps.
    // Activates automatically if `window.posthog` is initialised on the page
    // (e.g. via env-injected snippet); otherwise it's a no-op. No SDK weight
    // added unless the operator opts in.
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture("landing_cta_event", {
        $current_url: window.location.href,
        ab_variant: payload.variant,
        ab_action: payload.action,
        ab_cta_id: payload.cta_id,
        ab_page: payload.page,
        ab_tenant_slug: payload.tenant_slug,
        ab_visitor_id: payload.visitor_id,
        ab_lang: payload.lang,
      });
    }
  } catch (_e) { /* ignore */ }
}

export function useABVariant(ctaId, copies, opts = {}) {
  const { lang, tenantSlug, page = "landing" } = opts;
  // Fetch paused variants once; cached in sessionStorage to avoid an extra
  // request per page load while still letting masters revoke variants
  // within minutes.
  const paused = usePausedVariants(ctaId);
  const variant = useMemo(() => {
    // Pool is the intersection of declared copy keys and non-paused variants.
    const declared = Object.keys(copies);
    const allowed = declared.filter((v) => !paused.includes(v));
    const pool = allowed.length ? allowed : declared;
    const lsKey = `alp.ab.${ctaId}`;
    try {
      const cur = window.localStorage.getItem(lsKey);
      if (cur && pool.includes(cur)) return cur;
      const v = pool[Math.floor(Math.random() * pool.length)];
      window.localStorage.setItem(lsKey, v);
      return v;
    } catch (_e) {
      return pool[Math.floor(Math.random() * pool.length)];
    }
  }, [ctaId, copies, paused]);
  const visitorId = useMemo(() => getOrCreateVisitorId(), []);
  const impressionFired = useRef(false);

  useEffect(() => {
    if (impressionFired.current) return;
    impressionFired.current = true;
    fireEvent({
      variant, action: "impression", cta_id: ctaId,
      page, tenant_slug: tenantSlug || null,
      visitor_id: visitorId, lang: lang || null,
    });
  }, [variant, ctaId, page, tenantSlug, visitorId, lang]);

  const trackClick = () => {
    fireEvent({
      variant, action: "click", cta_id: ctaId,
      page, tenant_slug: tenantSlug || null,
      visitor_id: visitorId, lang: lang || null,
    });
  };

  return { variant, copy: copies[variant] ?? Object.values(copies)[0], trackClick, visitorId };
}

function usePausedVariants(ctaId) {
  const [paused, setPaused] = useState(() => {
    try {
      const cached = window.sessionStorage.getItem(`alp.ab.paused.${ctaId}`);
      return cached ? JSON.parse(cached) : [];
    } catch (_e) { return []; }
  });
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/analytics/variant-resolver/${encodeURIComponent(ctaId)}`)
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data?.paused_variants) ? data.paused_variants : [];
        setPaused(list);
        try { window.sessionStorage.setItem(`alp.ab.paused.${ctaId}`, JSON.stringify(list)); } catch (_e) { /* ignore */ }
      })
      .catch(() => { /* keep cached value on failure */ });
    return () => { alive = false; };
  }, [ctaId]);
  return paused;
}
